<template>
  <MarketingPageLayout>
    <h1 class="text-4xl md:text-5xl font-semibold tracking-tight text-ink mb-3">Blog</h1>
    <p class="text-lg text-ink-4 mb-12">
      Field notes on phone-heavy sales pipelines, CRM economics, and what we learn running NerdCRM internally at Nerddevs.
    </p>

    <p v-if="!posts.length" class="text-ink-4 italic">No posts published yet — the first one ships soon.</p>

    <ul v-else class="divide-y divide-line border-t border-line">
      <li
        v-for="post in posts"
        :key="post.slug"
        class="py-6"
      >
        <p
          v-if="post.publishedAt"
          class="text-sm text-ink-4 mb-1"
        >{{ formatDate(post.publishedAt) }}</p>
        <h2 class="text-2xl font-semibold text-ink mb-2">
          <router-link
            :to="`/blog/${post.slug}`"
            class="hover:text-accent transition-colors"
          >{{ post.title }}</router-link>
        </h2>
        <p
          v-if="post.description"
          class="text-ink-2 leading-relaxed"
        >{{ post.description }}</p>
      </li>
    </ul>
  </MarketingPageLayout>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { getAllContent } from '@/composables/useContent'
import { useSeo } from '@/composables/useSeo'
import MarketingPageLayout from '@/components/marketing/MarketingPageLayout.vue'
import { SITE_URL, WEBSITE_ID, orgRef } from '@/lib/seoEntities'

const posts = computed(() => getAllContent('blog'))

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

useSeo({
  title: 'Blog — NerdCRM field notes on phone-heavy sales',
  description:
    'Founder-written field notes on phone-heavy sales pipelines, CRM economics, and what we learn running NerdCRM at Nerddevs.',
  path: '/blog',
  rawTitle: true,
  jsonLd: {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': `${SITE_URL}/blog#blog`,
    name: 'NerdCRM blog',
    url: `${SITE_URL}/blog`,
    inLanguage: 'en',
    isPartOf: { '@id': WEBSITE_ID },
    publisher: orgRef,
    blogPost: posts.value.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      url: `${SITE_URL}/blog/${p.slug}`,
      ...(p.publishedAt ? { datePublished: p.publishedAt.toISOString() } : {}),
    })),
  },
})
</script>
